import { useEffect, useMemo, useRef, useState } from "react";
import { ChangeAlertCallbackType } from "../../../Root";
import { SpaceService } from "../../../../services/SpaceService";
import { statusTxsPending } from "../../../../util/DataObject";
import { useDialog } from '@albo-ui/core'
import { Typography, Tooltip, IconButton } from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
moment.locale("es");

export const useTxPending = (changeAlert: ChangeAlertCallbackType) => {
    const [data, setData] = useState([]);
    const [listTx, setListTx] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [action, setAction] = useState("");
    const [typeSearch, setTypeSearch] = useState("");
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [item, setItem] = useState(null);
    const [origin, setOrigin] = useState("spei");
    const [element, setElement] = useState(null);
    const [dialogText, setDialogText] = useState({});
    const user = JSON.parse(localStorage.session);
    const rol = JSON.parse(localStorage.config);

    const spaceService = useRef<SpaceService>();
    const dialogConfirm = useDialog();
    
    const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    });

    const saveData = async (result) => {
        setLoading(true);
        setData(result.content);
        setTotalDocuments(result.content.length || 0);
        setLoading(false);
    };

    const onHandleSearch = async ({value = null, type = null, filter = "spei"}) => {
        setLoading(true);
        setIsValid(false);
        setListTx([]);
        type && setTypeSearch(type);
        filter && setOrigin(filter);
    
        let data = {
            origin: filter === "cashin" ? ["pespay.cashin", "dapp.cashin", "paycash.cashin", "palla.cashin", "puntored.cashin" ] : [filter],
            status: ["pending"],
        };

        if (value) {
            data[type] = {
                value: [value]
            }
        }

        const response = await spaceService.current.spaceTxPending(data);
        if (response['data']?.response === 'OK') {
            saveData(response.data.widget[0].data);
            setLoading(false);
        } else {
            setLoading(false);
            changeAlert(true, 'error', response['message'] || response['data'].message || 'Error al realizar búsqueda');
        }
    };

    const onHandleConfirm = async () => {
        setLoading(true);
        setIsValid(false);
        setListTx([]);

        let newListTx = listTx.map((i) => {
            return {
                space: i["space"],
                uuid: i["uuid"],
                status: action
            }
        })


        let payload = {
            execute: newListTx,
            user: {
                email: user["email"],
                name: user["name"],
                uuid: user["uuid"],
                rol: rol["idRol"]
            }
        }

        const response = await spaceService.current.spaceTxPendingUpdate(payload);
        if (response['data']?.response === 'OK') {
            setLoading(false);
            changeAlert(true, 'success', 'Las Tx´s fueron actualizadas con éxito.');
            onHandleSearch({ filter: origin });
            setItem(null);
            setAction("");

        } else {
            setLoading(false);
            changeAlert(true, 'error', response['message'] || response['data'].message || 'Error al realizar actualizar Tx´s.');
        }
    };

    const onHandleConfirmUpdate = async (req) => {
        setLoading(true);
        setIsValid(false);
        setListTx([]);

        req["edate"] = new Date(req["edate"]);

        let payload = {
            execute: [{
                space: item["space"],
                uuid: item["uuid"],
                status: item["status"],
                action: req
            }],
            user: {
                email: user["email"],
                name: user["name"],
                uuid: user["uuid"],
                rol: rol["idRol"]
            }
        }

        const response = await spaceService.current.spaceTxPendingGeneralUpdate(payload);
        if (response['data']?.response === 'OK') {
            setLoading(false);
            changeAlert(true, 'success', 'La fecha fue actualizada con éxito.');
            onHandleSearch({ filter: origin });
            setItem(null);
            setAction("");
        } else {
            setLoading(false);
            changeAlert(true, 'error', response['message'] || response['data'].message || 'Error al realizar actualizar Tx´s.');
        }
    };

    const onHandleSelected = (data) => {
        const s = data.selectedRows;
        setListTx(s);
        s.length > 0 ? setIsValid(true) : setIsValid(false);
    };

    const onHandleUpdate = (row) => {
        row["action"]["edate"] = row["action"]["edate"].substring(0, 16);
        setItem(JSON.parse(JSON.stringify(row)));

        setElement([
            { type: "textField", label: "Fecha Expiración", key: "edate", textType: "datetime-local"}
        ]);
        setDialogText({
            title: "Actualizar Fecha",
            message: `A continuación, indique la nueva fecha de expiración correspondiente a la TX ${row.tracking_key} `,
            confirmButton: 'Actualizar'
        });
    
        dialogConfirm.openDialog();
    };

    const onOpenConfirm = (action) => {
        setAction(action);
        let check = action === "rejected" ? "rechazar" : "aprobar"
        setElement([
            {type: "list", list: listTx, key: "space", text: `Listado de Tx´s para ${check}`}
        ]);
        setDialogText({
            title: "Actualizar Tx´s Pendientes",
            message: `¿Confirma que desea ${check} las transacciones indicadas? Se actualizaran ${listTx.length} Tx´s.`,
            confirmButton: check.charAt(0).toUpperCase() + check.slice(1)
        });
    
        dialogConfirm.openDialog();
    };
    const onCloseDialogConfirm = () => {
        dialogConfirm.onClose();
        setAction("");
        setItem(null);
    };

    const onConfirm = (payload?) => {
        dialogConfirm.onClose();
        return payload ? onHandleConfirmUpdate(payload) : onHandleConfirm();
    };

    useEffect(() => {
        spaceService.current = new SpaceService(changeAlert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculateColumns = useMemo(() => {
        const columns = [
            { selector: (row) => row["tracking_key"], name: "Tracking Key", wrap: true, sortable: true, width: "14%" },
            { selector: (row) => row["space"], name: "Space", wrap: true, sortable: true },
            { selector: (row) => row["origin"], name: "Origen", wrap: true, sortable: true, width: "10%" },
            { selector: (row) => row["detail"], name: "Detalle", wrap: true, sortable: true },
            { selector: (row) => formatter.format(row["amount"]), name: "Monto", wrap: true, sortable: true, width: "10%" },
            { selector: (row) => statusTxsPending.filter((item) => row.status === item.key)[0].label || "N/A", name: "Estatus", wrap: true, sortable: true, width: "9%" },
            { selector: (row) => moment.utc(row.cdate).format("L, HH:mm:ss"), name: "Fecha creación", wrap: true, sortable: true, width: "10%" },
            { selector: (row) => (row["action"] && row["action"]["edate"] && moment.utc(row.action.edate).format("L, HH:mm:ss")) || "---", name: "Fecha ejecución", wrap: true, sortable: true, width: "10%" },
            { cell: (row) => {
                return row["action"] && row["action"]["edate"] ? <Typography fontWeight="400" fontFamily="Muli,sans-serif" color="#000000de" fontSize="0.875rem">
                    <Tooltip title='Editar'>
                        <IconButton onClick={onHandleUpdate.bind(this, row)}>
                            <EditIcon/>
                        </IconButton>
                    </Tooltip>
                </Typography> : "---"
            }, name: 'Acciones', width: '6%'
            }
        ];
        
        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    return {
        data,
        item,
        loading,
        isValid,
        typeSearch,
        origin,
        element,
        dialogText,
        dialogConfirm,
        totalDocuments,
        columns: calculateColumns,
        onHandleSearch,
        onHandleSelected,
        saveData,
        onConfirm,
        onOpenConfirm,
        onCloseDialogConfirm
    };
};
